/**
 * Minimal History-API client router. Route patterns use `:param` segments;
 * resolve() matches longest prefix, returns a handler bound to params.
 */

import { mount } from '@/lib/dom';
import { ensureSession } from '@/lib/session';

export interface RouteContext {
  params: Record<string, string>;
  query: URLSearchParams;
  path: string;
}

export type RouteHandler = (ctx: RouteContext) => Node | Promise<Node>;

interface RouteDef {
  pattern: string;
  segments: string[];
  handler: RouteHandler;
  auth: 'required' | 'anonymous';
}

const routes: RouteDef[] = [];
let rootEl: HTMLElement | null = null;

// Scroll restoration. Saved per-path in sessionStorage so back-nav from
// /labs/:key returns to the same scroll position on /labs.
const SCROLL_KEY = 'hd:scroll';
let scrollMap: Record<string, number> = {};
try {
  scrollMap = JSON.parse(sessionStorage.getItem(SCROLL_KEY) ?? '{}');
} catch {
  scrollMap = {};
}
let lastResolvedPath: string = window.location.pathname;
let isPopstate = false;
let pendingScrollTarget: number | null = null;

function persistScrollMap(): void {
  try {
    sessionStorage.setItem(SCROLL_KEY, JSON.stringify(scrollMap));
  } catch {
    /* quota exhausted — ignore */
  }
}

function saveScrollFor(path: string): void {
  scrollMap[path] = window.scrollY;
  persistScrollMap();
}

if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}

document.addEventListener('route:settled', () => {
  if (pendingScrollTarget !== null) {
    window.scrollTo(0, pendingScrollTarget);
    pendingScrollTarget = null;
  }
});

// Pages register per-view cleanups (ResizeObserver.disconnect, uPlot.destroy,
// etc.) here. Router drains the list right before mounting the next view —
// DOM removal alone doesn't free global API refs like ResizeObserver.
let currentCleanups: Array<() => void> = [];

export function registerCleanup(fn: () => void): void {
  currentCleanups.push(fn);
}

function runCleanups(): void {
  const fns = currentCleanups;
  currentCleanups = [];
  for (const fn of fns) {
    try {
      fn();
    } catch (err) {
      console.warn('[router] cleanup error', err);
    }
  }
}

export function registerRoute(
  pattern: string,
  handler: RouteHandler,
  opts: { auth?: 'required' | 'anonymous' } = {},
): void {
  routes.push({
    pattern,
    segments: pattern.split('/').filter(Boolean),
    handler,
    auth: opts.auth ?? 'required',
  });
}

interface MatchResult {
  route: RouteDef;
  params: Record<string, string>;
}

function matchRoute(path: string): MatchResult | null {
  const segments = path.split('?')[0]!.split('/').filter(Boolean);
  for (const route of routes) {
    if (route.segments.length !== segments.length) continue;
    const params: Record<string, string> = {};
    let ok = true;
    for (let i = 0; i < route.segments.length; i++) {
      const rs = route.segments[i]!;
      const ps = segments[i]!;
      if (rs.startsWith(':')) {
        params[rs.slice(1)] = decodeURIComponent(ps);
      } else if (rs !== ps) {
        ok = false;
        break;
      }
    }
    if (ok) return { route, params };
  }
  return null;
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  saveScrollFor(lastResolvedPath);
  if (opts.replace) {
    window.history.replaceState({}, '', path);
  } else {
    window.history.pushState({}, '', path);
  }
  void resolveCurrent();
}

export async function resolveCurrent(): Promise<void> {
  if (!rootEl) return;
  runCleanups();
  const path = window.location.pathname + window.location.search;
  const match = matchRoute(window.location.pathname);
  if (!match) {
    const notFound = await renderNotFound();
    mount(rootEl, notFound);
    return;
  }
  const { route, params } = match;
  if (route.auth === 'required') {
    const user = await ensureSession();
    if (!user) {
      const here = window.location.pathname + window.location.search;
      if (here !== '/login') {
        navigate(`/login?next=${encodeURIComponent(here)}`, { replace: true });
        return;
      }
    }
  }
  if (route.auth === 'anonymous') {
    const user = await ensureSession();
    if (user) {
      navigate('/', { replace: true });
      return;
    }
  }
  const ctx: RouteContext = {
    params,
    query: new URLSearchParams(window.location.search),
    path,
  };
  try {
    const node = await route.handler(ctx);
    mount(rootEl, node);
    const newPath = window.location.pathname;
    const restored = isPopstate ? scrollMap[newPath] : undefined;
    if (restored != null) {
      pendingScrollTarget = restored;
      window.scrollTo(0, restored);
    } else {
      pendingScrollTarget = null;
      window.scrollTo(0, 0);
    }
    isPopstate = false;
    lastResolvedPath = newPath;
  } catch (err) {
    console.error('[router] handler error', err);
    mount(rootEl, await renderError(err));
  }
}

export function startRouter(el: HTMLElement): void {
  rootEl = el;
  window.addEventListener('popstate', () => {
    saveScrollFor(lastResolvedPath);
    isPopstate = true;
    void resolveCurrent();
  });
  document.addEventListener('click', (ev) => {
    if (ev.defaultPrevented) return;
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    const link = target.closest?.('a[href]');
    if (!link) return;
    const href = link.getAttribute('href');
    if (!href || href.startsWith('http') || href.startsWith('//') || href.startsWith('mailto:') || href.startsWith('#')) return;
    if (link.getAttribute('target') === '_blank') return;
    if (link.hasAttribute('data-external')) return;
    if ((ev as MouseEvent).metaKey || (ev as MouseEvent).ctrlKey || (ev as MouseEvent).shiftKey) return;
    ev.preventDefault();
    navigate(href);
  });
  void resolveCurrent();
}

async function renderNotFound(): Promise<Node> {
  const el = document.createElement('div');
  el.className = 'empty-state';
  el.innerHTML = '<h1>404</h1><p>Страница не найдена.</p><a href="/">На главную</a>';
  return el;
}

async function renderError(err: unknown): Promise<Node> {
  const el = document.createElement('div');
  el.className = 'empty-state';
  const message = err instanceof Error ? err.message : 'Неизвестная ошибка';
  el.innerHTML = `<h1>Ошибка</h1><p>${escapeHtml(message)}</p>`;
  return el;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
